import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import './DashboardPages.css';

const ACTION_TYPES = {
  login: 'auth',
  password_changed: 'auth',
  order_created: 'order',
  order_create_failed: 'order',
  order_status_changed: 'order',
  driver_assigned: 'trip',
  trip_status_changed: 'trip',
  pickup_confirmed: 'trip',
  delivery_confirmed: 'trip',
  driver_pickup: 'trip',
  driver_delivery: 'trip',
  trip_updated: 'trip',
  farmer_registered: 'info',
  farmer_updated: 'info',
  farmer_deleted: 'info',
  driver_registered: 'info',
  driver_updated: 'info',
  driver_deleted: 'info',
  service_area_updated: 'info',
  contact_message_received: 'info',
  contact_message_deleted: 'info',
};

const ACTION_LABELS = {
  order_created: 'Order created',
  order_create_failed: 'Order create failed',
  order_status_changed: 'Order status changed',
  driver_assigned: 'Driver assigned',
  trip_status_changed: 'Trip status changed',
  pickup_confirmed: 'Pickup confirmed',
  delivery_confirmed: 'Delivery confirmed',
  driver_pickup: 'Pickup confirmed',
  driver_delivery: 'Delivery confirmed',
  trip_updated: 'Trip updated',
  login: 'Admin login',
  password_changed: 'Password changed',
  farmer_registered: 'Farmer registered',
  farmer_updated: 'Farmer updated',
  farmer_deleted: 'Farmer deleted',
  driver_registered: 'Driver registered',
  driver_updated: 'Driver updated',
  driver_deleted: 'Driver deleted',
  service_area_updated: 'Service area updated',
  contact_message_received: 'Contact form received',
  contact_message_deleted: 'Contact message deleted',
};

/** fixes.txt #4 — required operational event types */
const MILESTONE_CORE_ACTIONS = [
  { key: 'order_created', label: 'Order created' },
  { key: 'driver_assigned', label: 'Driver assigned' },
  { key: 'order_status_changed', label: 'Order status changed' },
  { key: 'delivery_confirmed', label: 'Delivery confirmed' },
];

const POLL_MS = 30000;

function formatRelativeTime(createdAt) {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatAbsoluteTime(createdAt);
}

function formatAbsoluteTime(createdAt) {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function ActivityLogPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('core');

  const load = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);

      let res;
      if (filter === 'core') {
        res = await api.getActivity(150, false, true);
      } else if (filter === 'operations') {
        res = await api.getActivity(100, true, false);
      } else {
        res = await api.getActivity(100, false, false);
      }

      let rows = res.activities || [];
      if (filter === 'auth') {
        rows = rows.filter((a) => ACTION_TYPES[a.action] === 'auth');
      }
      setActivities(rows);
    } catch (err) {
      setError(err.message);
      setActivities([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const seenCoreTypes = useMemo(() => {
    const set = new Set(activities.map((a) => a.action));
    return MILESTONE_CORE_ACTIONS.map((item) => ({
      ...item,
      seen: set.has(item.key),
    }));
  }, [activities]);

  return (
    <div className="dashboard-page">
      <h1>Activity Log</h1>
      <p className="dashboard-subtitle">
        Operational traceability for Milestone 1 — every event includes who triggered it and an exact
        timestamp.
      </p>

      <section className="entity-manual-panel activity-core-panel" aria-labelledby="activity-core-heading">
        <h2 id="activity-core-heading" className="entity-manual-title">
          Required operational events (contract)
        </h2>
        <p className="orders-hint entity-manual-hint">
          These four event types must appear as you run real operations (WhatsApp order → assign driver
          → pickup → delivery):
        </p>
        <ul className="activity-core-checklist">
          {seenCoreTypes.map((item) => (
            <li key={item.key} className={item.seen ? 'activity-core-seen' : 'activity-core-missing'}>
              <span className="activity-core-mark">{item.seen ? '✓' : '○'}</span>
              {item.label}
              <code className="activity-core-code">{item.key}</code>
            </li>
          ))}
        </ul>
      </section>

      <div className="orders-toolbar">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="orders-filter"
          aria-label="Filter activity log"
        >
          <option value="core">Core logistics (Milestone 1)</option>
          <option value="operations">All operations</option>
          <option value="all">All events</option>
          <option value="auth">Admin login only</option>
        </select>
        <button
          type="button"
          className="orders-refresh-btn"
          onClick={() => load({ silent: true })}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh log'}
        </button>
      </div>

      {error && <div className="orders-error">{error}</div>}

      {loading ? (
        <div className="orders-placeholder">
          <p>Loading activity…</p>
        </div>
      ) : activities.length === 0 ? (
        <div className="orders-placeholder">
          <p>No events yet for this filter.</p>
          <p className="orders-hint">
            Create an order on WhatsApp, assign a driver on Orders, then confirm pickup/delivery on
            the driver WhatsApp — each step writes to this log.
          </p>
        </div>
      ) : (
        <div className="activity-log">
          <p className="orders-hint activity-summary">
            {activities.length} event{activities.length !== 1 ? 's' : ''} — each row shows relative
            time and full date/time to the second.
          </p>
          <div className="activity-log-list">
            {activities.map((a) => (
              <div
                key={a.id}
                className={`activity-item activity-${ACTION_TYPES[a.action] || 'info'}`}
              >
                <div className="activity-dot" />
                <div className="activity-content">
                  <p className="activity-type">{ACTION_LABELS[a.action] || a.action}</p>
                  <p className="activity-action">{a.description || '—'}</p>
                  <span className="activity-meta">
                    {a.user} · {formatRelativeTime(a.created_at)} · {formatAbsoluteTime(a.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLogPage;
